import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { SEO } from "../components/SEO";
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, addMonths, subMonths, isToday as checkIsToday } from "date-fns";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Plus,
  Pencil,
  Trash2,
  Bell,
  Mail,
  Loader2,
  Clock,
  X,
} from "lucide-react";

const CalendarPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNoteDialog, setShowNoteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const emptyNote = {
    title: "",
    description: "",
    date: "",
    time: "",
    send_email_reminder: false,
    reminder_email: user?.email || "",
    reminder_days_before: 1,
  };

  const [newNote, setNewNote] = useState(emptyNote);
  const [editNote, setEditNote] = useState(emptyNote);

  // Get days of the current month
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const days = eachDayOfInterval({ start: monthStart, end: monthEnd });

  // Get day of week for first day (0 = Sunday)
  const startDayOfWeek = monthStart.getDay();

  // Fetch notes
  useEffect(() => {
    const loadNotes = async () => {
      try {
        const response = await axios.get(`${API}/calendar/notes`, { withCredentials: true });
        setNotes(response.data);
      } catch (error) {
        setNotes([]);
      } finally {
        setLoading(false);
      }
    };
    loadNotes();
  }, []);

  const handleAddNote = async () => {
    if (!newNote.title || !newNote.date) {
      toast.error("Please enter a title and date");
      return;
    }

    if (newNote.send_email_reminder && !newNote.reminder_email) {
      toast.error("Please enter an email for reminder");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.post(`${API}/calendar/notes`, newNote, {
        withCredentials: true,
      });
      setNotes([...notes, response.data]);
      toast.success("Note added successfully");
      setShowNoteDialog(false);
      setNewNote(emptyNote);
    } catch (error) {
      toast.error("Failed to add note");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateNote = async () => {
    if (!editNote.title || !editNote.date) {
      toast.error("Please enter a title and date");
      return;
    }

    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/calendar/notes/${selectedNote.note_id}`,
        editNote,
        { withCredentials: true }
      );
      setNotes(notes.map((n) => 
        n.note_id === selectedNote.note_id ? response.data : n
      ));
      toast.success("Note updated successfully");
      setShowEditDialog(false);
      setSelectedNote(null);
    } catch (error) {
      toast.error("Failed to update note");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async () => {
    if (!selectedNote) return;

    setDeleting(true);
    try {
      await axios.delete(`${API}/calendar/notes/${selectedNote.note_id}`, {
        withCredentials: true,
      });
      setNotes(notes.filter((n) => n.note_id !== selectedNote.note_id));
      toast.success("Note deleted");
      setShowDeleteDialog(false);
      setSelectedNote(null);
    } catch (error) {
      toast.error("Failed to delete note");
    } finally {
      setDeleting(false);
    }
  };

  const getNotesForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return notes.filter((note) => note.date === dateStr);
  };

  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  const openAddDialog = (date) => {
    setNewNote({
      ...emptyNote,
      date: format(date, "yyyy-MM-dd"),
      reminder_email: user?.email || "",
    });
    setShowNoteDialog(true);
  };

  const openEditDialog = (note) => {
    setSelectedNote(note);
    setEditNote({
      title: note.title || "",
      description: note.description || "",
      date: note.date || "",
      time: note.time || "",
      send_email_reminder: note.send_email_reminder || false,
      reminder_email: note.reminder_email || user?.email || "",
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (note) => {
    setSelectedNote(note);
    setShowDeleteDialog(true);
  };

  // Note form component
  const NoteForm = ({ note, setNote, onSubmit, isEdit = false }) => (
    <div className="space-y-4">
      <div>
        <Label className="text-zinc-400">Title *</Label>
        <Input
          className="glass-input text-lg"
          placeholder="Trip to Paris, Hotel check-in, etc."
          value={note.title}
          onChange={(e) => setNote({ ...note, title: e.target.value })}
          autoComplete="off"
          data-testid="note-title-input"
        />
      </div>

      <div>
        <Label className="text-zinc-400">Description</Label>
        <Textarea
          className="glass-input min-h-[100px]"
          placeholder="Add details about this event..."
          value={note.description}
          onChange={(e) => setNote({ ...note, description: e.target.value })}
          data-testid="note-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-zinc-400">Date *</Label>
          <Input
            type="date"
            className="glass-input"
            value={note.date}
            onChange={(e) => setNote({ ...note, date: e.target.value })}
            data-testid="note-date-input"
          />
        </div>
        <div>
          <Label className="text-zinc-400">Time (optional)</Label>
          <Input
            type="time"
            className="glass-input"
            value={note.time}
            onChange={(e) => setNote({ ...note, time: e.target.value })}
            data-testid="note-time-input"
          />
        </div>
      </div>

      <div className="p-4 bg-slate-800/50 rounded-xl space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-500/20 flex items-center justify-center">
              <Mail className="w-5 h-5 text-sky-400" />
            </div>
            <div>
              <span className="text-white text-sm font-medium">Email Reminder</span>
              <p className="text-xs text-zinc-500">Get notified on this date</p>
            </div>
          </div>
          <Switch
            checked={note.send_email_reminder}
            onCheckedChange={(checked) => setNote({ ...note, send_email_reminder: checked })}
            data-testid="email-reminder-switch"
          />
        </div>
        {note.send_email_reminder && (
          <div className="space-y-2">
            <Input
              type="email"
              className="glass-input"
              placeholder="your@email.com"
              value={note.reminder_email}
              onChange={(e) => setNote({ ...note, reminder_email: e.target.value })}
              autoComplete="off"
              data-testid="reminder-email-input"
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-zinc-400 whitespace-nowrap">Remind me</span>
              <select
                className="flex-1 glass-input text-sm h-9 rounded-md bg-white/5 border-white/10 text-white px-2"
                value={note.reminder_days_before || 1}
                onChange={(e) => setNote({ ...note, reminder_days_before: parseInt(e.target.value) })}
                data-testid="reminder-days-select"
              >
                <option value={0}>On the day</option>
                <option value={1}>1 day before</option>
                <option value={2}>2 days before</option>
                <option value={3}>3 days before</option>
                <option value={7}>1 week before</option>
                <option value={14}>2 weeks before</option>
              </select>
            </div>
          </div>
        )}
      </div>

      <Button
        className="w-full btn-primary h-12 text-base"
        onClick={onSubmit}
        disabled={saving}
        data-testid={isEdit ? "update-note-btn" : "save-note-btn"}
      >
        {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : null}
        {isEdit ? "Update Note" : "Save Note"}
      </Button>
    </div>
  );

  if (loading) {
    return (
      <div className="page-container flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-sky-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="page-container pb-28" data-testid="calendar-page">
      <SEO title="Travel Calendar" description="Manage your travel calendar, plan activities, and get automatic email reminders for upcoming trips." path="/calendar" />
      <div className="max-w-4xl mx-auto">
        {/* Hero Banner */}
        <div className="relative h-32 sm:h-40 overflow-hidden mb-6">
          <img src="https://images.unsplash.com/photo-1506784983877-45594efa4cbe?w=1200&q=80" alt="" className="w-full h-full object-cover" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#080d19]/80 via-[#080d19]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-4 pb-5 flex items-end justify-between">
            <div>
              <h1 className="text-2xl font-bold font-heading text-white drop-shadow-lg">My Calendar</h1>
              <p className="text-sm text-slate-300/80 mt-0.5 drop-shadow">Plan your travel dates & events</p>
            </div>
            <Button
              onClick={goToToday}
              variant="outline"
              className="btn-outline text-sm"
              data-testid="go-to-today-btn"
            >
              Today
            </Button>
          </div>
        </div>
        <div className="px-4">

        {/* Calendar Card */}
        <div className="glass-card p-6 mb-6">
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={prevMonth}
              className="p-3 hover:bg-white/5 rounded-xl transition-colors"
              data-testid="prev-month-btn"
            >
              <ChevronLeft className="w-6 h-6 text-white" />
            </button>
            <h2 className="text-2xl font-bold text-white">
              {format(currentDate, "MMMM yyyy")}
            </h2>
            <button
              onClick={nextMonth}
              className="p-3 hover:bg-white/5 rounded-xl transition-colors"
              data-testid="next-month-btn"
            >
              <ChevronRight className="w-6 h-6 text-white" />
            </button>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-2 mb-3">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div key={day} className="text-center text-sm font-medium text-zinc-400 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid - Bigger Numbers */}
          <div className="grid grid-cols-7 gap-2">
            {/* Empty cells for days before start of month */}
            {Array.from({ length: startDayOfWeek }).map((_, i) => (
              <div key={`empty-${i}`} className="aspect-square" />
            ))}

            {/* Days of the month */}
            {days.map((day) => {
              const dayNotes = getNotesForDate(day);
              const isToday = checkIsToday(day);
              const isSelected = selectedDate && isSameDay(day, selectedDate);
              const hasNotes = dayNotes.length > 0;

              return (
                <button
                  key={day.toString()}
                  onClick={() => setSelectedDate(day)}
                  className={`
                    aspect-square rounded-xl flex flex-col items-center justify-center 
                    relative transition-all duration-200 group
                    ${isToday
                      ? "bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-lg shadow-violet-500/30"
                      : isSelected
                      ? "bg-sky-500/20 text-white ring-2 ring-sky-500/50"
                      : "hover:bg-white/5 text-zinc-300"
                    }
                  `}
                  data-testid={`calendar-day-${format(day, "yyyy-MM-dd")}`}
                >
                  {/* Journal pencil icon */}
                  <span
                    className={`absolute top-1 right-1 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white/10 ${isToday ? "text-white/70 hover:text-white" : "text-amber-400/70 hover:text-amber-400"}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/journal?date=${format(day, "yyyy-MM-dd")}`);
                    }}
                    data-testid={`journal-day-${format(day, "yyyy-MM-dd")}`}
                    title="Write journal"
                  >
                    <Pencil className="w-3 h-3" />
                  </span>

                  {/* Big Day Number */}
                  <span className={`
                    text-2xl font-bold
                    ${isToday ? "text-white" : isSelected ? "text-sky-400" : "text-zinc-200 group-hover:text-white"}
                  `}>
                    {format(day, "d")}
                  </span>
                  
                  {/* Note indicators */}
                  {hasNotes && (
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                      {dayNotes.slice(0, 3).map((_, i) => (
                        <div
                          key={i}
                          className={`w-1.5 h-1.5 rounded-full ${
                            isToday ? "bg-white/80" : "bg-emerald-400"
                          }`}
                        />
                      ))}
                      {dayNotes.length > 3 && (
                        <span className="text-[10px] text-emerald-400 ml-0.5">+{dayNotes.length - 3}</span>
                      )}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Selected Date Section */}
        <div className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">
                {selectedDate 
                  ? format(selectedDate, "EEEE, MMMM d, yyyy")
                  : "Select a date"
                }
              </h3>
              {selectedDate && getNotesForDate(selectedDate).length > 0 && (
                <p className="text-sm text-zinc-400">
                  {getNotesForDate(selectedDate).length} note{getNotesForDate(selectedDate).length !== 1 ? 's' : ''}
                </p>
              )}
            </div>
            {selectedDate && (
              <Button
                className="btn-primary"
                onClick={() => openAddDialog(selectedDate)}
                data-testid="add-note-btn"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Note
              </Button>
            )}
          </div>

          {!selectedDate ? (
            <div className="text-center py-12">
              <CalendarIcon className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">Click on a date to view or add notes</p>
            </div>
          ) : getNotesForDate(selectedDate).length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 rounded-2xl bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <Plus className="w-8 h-8 text-zinc-500" />
              </div>
              <p className="text-zinc-400 mb-4">No notes for this date</p>
              <Button
                variant="outline"
                className="btn-outline"
                onClick={() => openAddDialog(selectedDate)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Create a note
              </Button>
            </div>
          ) : (
            <ScrollArea className="max-h-[400px]">
              <div className="space-y-3">
                {getNotesForDate(selectedDate).map((note) => (
                  <div
                    key={note.note_id}
                    className="p-4 bg-slate-800/50 rounded-xl border border-white/5 hover:border-white/10 transition-colors"
                    data-testid={`note-${note.note_id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-white text-lg truncate">
                          {note.title}
                        </h4>
                        {note.description && (
                          <p className="text-zinc-400 text-sm mt-1 line-clamp-2">
                            {note.description}
                          </p>
                        )}
                        <div className="flex items-center gap-4 mt-3">
                          {note.time && (
                            <div className="flex items-center gap-1.5 text-xs text-zinc-500">
                              <Clock className="w-3.5 h-3.5" />
                              {note.time}
                            </div>
                          )}
                          {note.send_email_reminder && (
                            <div className="flex items-center gap-1.5 text-xs text-sky-400">
                              <Bell className="w-3.5 h-3.5" />
                              Reminder
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {note.send_email_reminder && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-9 w-9 text-sky-400 hover:text-sky-300 hover:bg-sky-500/10"
                            onClick={async () => {
                              try {
                                await axios.post(`${API}/calendar/notes/${note.note_id}/send-reminder`, {}, { withCredentials: true });
                                toast.success("Reminder email sent!");
                              } catch (e) { toast.error("Failed to send reminder"); }
                            }}
                            data-testid={`send-reminder-${note.note_id}`}
                            title="Send reminder email now"
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-9 w-9 text-zinc-400 hover:text-white hover:bg-white/10"
                          onClick={() => openEditDialog(note)}
                          data-testid={`edit-note-${note.note_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-9 w-9 text-zinc-400 hover:text-red-400 hover:bg-red-500/10"
                          onClick={() => openDeleteDialog(note)}
                          data-testid={`delete-note-${note.note_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>

      {/* Add Note Dialog */}
      <Dialog open={showNoteDialog} onOpenChange={setShowNoteDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Plus className="w-5 h-5 text-emerald-400" />
              Add Note
            </DialogTitle>
          </DialogHeader>
          <NoteForm
            note={newNote}
            setNote={setNewNote}
            onSubmit={handleAddNote}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Note Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="glass-card border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Pencil className="w-5 h-5 text-sky-400" />
              Edit Note
            </DialogTitle>
          </DialogHeader>
          <NoteForm
            note={editNote}
            setNote={setEditNote}
            onSubmit={handleUpdateNote}
            isEdit
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="glass-card border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Note</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete "{selectedNote?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteNote}
              className="bg-red-500 hover:bg-red-600 text-white"
              disabled={deleting}
              data-testid="confirm-delete-note"
            >
              {deleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
    </div>
  );
};

export default CalendarPage;
